import { useCallback, useRef } from "react";

interface UseLongPressOptions {
  /** Duration in ms before long-press fires. Default 450ms. */
  threshold?: number;
  /** Pixel movement that cancels the long-press (treated as a scroll). */
  moveTolerance?: number;
}

/**
 * Mobile long-press handler. Returns pointer event props that fire `onLongPress`
 * after the user holds for `threshold` ms without moving more than
 * `moveTolerance` pixels. Designed for tap-cards where the primary tap should
 * still navigate — the consumer must call `cancel()` on click if a long-press
 * fired, by checking `firedRef.current`.
 */
export function useLongPress(
  onLongPress: () => void,
  { threshold = 450, moveTolerance = 8 }: UseLongPressOptions = {}
) {
  const timer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);

  const clear = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    start.current = null;
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Only react to touch / pen. Mouse users get the kebab button instead.
      if (e.pointerType === "mouse") return;
      fired.current = false;
      start.current = { x: e.clientX, y: e.clientY };
      timer.current = window.setTimeout(() => {
        fired.current = true;
        // Haptic nudge where supported.
        if (typeof navigator !== "undefined" && "vibrate" in navigator) {
          try {
            navigator.vibrate?.(10);
          } catch {
            /* ignore */
          }
        }
        onLongPress();
      }, threshold);
    },
    [onLongPress, threshold]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!start.current) return;
      const dx = e.clientX - start.current.x;
      const dy = e.clientY - start.current.y;
      if (Math.hypot(dx, dy) > moveTolerance) clear();
    },
    [clear, moveTolerance]
  );

  return {
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: clear,
      onPointerCancel: clear,
      onPointerLeave: clear,
    },
    /** True when the most recent gesture triggered the long-press callback. */
    didFireRef: fired,
  };
}
